/* silent-click-detector.js — effect-based silent-click telemetry.
 *
 * Extracted from layouts/app.blade.php inline analytics on 2026-05-16.
 * The inline version was intermittently truncated by the MinifyHtml
 * regex minifier — a large inline script is fragile through that
 * pipeline. An external file is never processed by MinifyHtml.
 *
 * Flags clicks on action-shaped elements that produce NO observable
 * effect within 2s and reports feature_use:silent_click {det:2}.
 *
 * v1 (the version this replaces) used "no meaningful __ae within 3s"
 * as the proxy for "nothing happened". That was wrong ~80% of the
 * time: most working affordances never emit __ae — opening the OS
 * file picker, starting playback, dismissing the cookie banner — so
 * v1 false-fired on every one of them (top offender: the upload zone
 * itself, 1740 hits / 1454 sessions over 30d), drowning the signal
 * and poisoning last_action on every feedback report.
 *
 * v2 measures the click's ACTUAL effect inside a 2s window. The click
 * "did something" if ANY of: a real DOM mutation (modal/toast/dropdown
 * insert, class/aria/hidden flip, banner removal); a window blur
 * (native dialog opened — file picker / print); an on-page navigation;
 * a meaningful __ae. None of those => the click genuinely did nothing
 * => a real silent click.
 */
(function () {
    'use strict';

    // window.__scdStatus is a diagnostic marker — the last gate reached.
    // 'armed' means the click listener is live; anything else is why not.
    window.__scdStatus = 'start';

    // Two readers of one judgement. The silent_click REPORT is telemetry and
    // waits for analytics consent, exactly as before. The intl tool showcase
    // (layouts/app, body.tool-showcase) reveals a tool's workspace before any
    // file, so its Play / Save / Export are pressable and answer nothing; the
    // page answers such a press itself (`tmb:silent-click`, below) — and that
    // answer is not telemetry: nothing leaves the browser for it, so on a
    // showcase page the judge runs whatever the consent says. Elsewhere a page
    // without consent installs nothing, as before.
    var answers = !!(document.body && document.body.classList.contains('tool-showcase'));
    var report = true, gate = '';

    // Consent gate — mirror of the layout's consentOK(): DNT off, not
    // declined, and no pending cookie-banner choice.
    try {
        var consent = localStorage.getItem('cookie_consent');
        var banner = document.getElementById('ccBanner');
        if (navigator.doNotTrack === '1') gate = 'dnt';
        else if (consent === 'declined') gate = 'declined';
        else if (banner && banner.classList.contains('cc-visible') && consent !== 'accepted') gate = 'banner-pending';
    } catch (_) { gate = 'consent-error'; }
    if (gate) { report = false; if (!answers) { window.__scdStatus = gate; return; } }

    var meta = document.querySelector('meta[name="tool-slug"]');
    var slug = meta ? meta.content : null;
    if (!slug) { window.__scdStatus = 'no-slug'; return; }
    if (typeof window.__ae !== 'function') { report = false; if (!answers) { window.__scdStatus = 'no-ae'; return; } }

    var ua = navigator.userAgent || '';
    var mob = /Mobi|Android|iPhone|iPad/i.test(ua) ? 1 : 0;
    // Третья копия лестницы браузеров жила здесь и тоже разъехалась с главной
    // (ни 'chr-ios', ни вендорских оболочек). Теперь все три зовут ОДИН
    // классификатор из telemetry-tier0.js; он гарантированно определён —
    // выше мы уже вышли, если нет window.__ae из того же файла.
    var br = window.__brOf ? window.__brOf(ua) : 'other';

    // Dedup per-session per-target so rage-clicking the same button
    // doesn't fire 10x silent_clicks.
    var seen = {};

    // ── File picker: the one effect this detector could not see ──────────
    // Opening the OS file dialog was supposed to be caught by case 2 below
    // (window `blur` — "native dialog steals focus"). It is not: measured
    // 2026-08-13, clicking a shared uploader fires NO window blur inside the
    // 2 s window, while the click DOES land on the <input type=file>.
    //
    // The cost of missing it was that every upload zone on the site looked
    // broken: 8539 of 57232 silent_click events over 30 days (15% of the whole
    // metric) sat on upload zones, and 93-96% of those sessions went on to pick
    // a file in the same session — enhance-video 955 of 1002, join 259 of 282,
    // video-colorize 29 of 31. `evUpload` was the #1 entry in the whole
    // leaderboard, purely as noise, which buries any genuinely dead button
    // under it.
    //
    // A click reaching <input type=file> is the reliable signal: both the
    // native <label for> path and our own input.click() dispatch a bubbling
    // click on the input, regardless of how focus behaves.
    //
    // ⚠️ This deliberately also swallows the case where input.click() is
    // dispatched but the browser refuses to SHOW the dialog (no user
    // activation). That case is not lost — it has a purpose-built detector
    // with a 30 s window and visibility+focus signals:
    // `upload_no_pick_followup{state:'no_open'}` in tool-uploader.js, 554
    // sessions/30d. Two instruments, one job each; this one was doing that job
    // at 5% precision and drowning its own.
    //
    // Ordering matters: the uploader's handler sits on the zone ELEMENT, so
    // input.click() has already fired by the time this module's document-level
    // bubble handler arms. Listening only from inside that handler would miss
    // it every time. So both timestamps are taken from one permanent CAPTURE
    // listener, which sees the outer click before the uploader reacts and the
    // nested input click as it happens.
    var lastDispatchAt = 0;      // when the current user click began propagating
    var lastPointerDownAt = 0;   // when the pointer went DOWN for that click
    var lastPickerOpenAt = -1;   // when a click last landed on <input type=file>
    document.addEventListener('pointerdown', function () { lastPointerDownAt = Date.now(); }, true);
    document.addEventListener('click', function (e) {
        var t = e.target;
        if (t && t.tagName === 'INPUT' && t.type === 'file') { lastPickerOpenAt = Date.now(); return; }
        lastDispatchAt = Date.now();
    }, true);

    // Track the timestamp of the last meaningful __ae. This script is
    // deferred, so window.__ae is already fully assembled by the inline
    // analytics by the time we run — one wrap is stable.
    var lastMeaningfulAe = Date.now();
    var meaningless = {
        tool_interact: 1, rage_click: 1, scroll_depth: 1, time_on_page: 1,
        ad_impression: 1, ad_failed: 1, ad_error: 1, session_start: 1,
        device_cap: 1, page_view: 1, silent_click: 1,
        // A web vital is a MEASUREMENT of the page, not a response to a click,
        // and it fires on its own schedule (LCP, CLS, and one per interaction).
        // Counting it as proof of an effect exonerated whatever was clicked
        // near it — that is the detector failing to fire, which is the failure
        // it exists to prevent. It also made its own guard flaky.
        web_vital: 1
    };
    // …and `feature_use` that page infrastructure fires on ANY gesture: the
    // audio unlockers report on the first tap of every page, whatever was
    // pressed — counted, they cleared each first press (on iOS, every page).
    var meaninglessF = {
        silent_click: 1, audio_gate_unlock: 1, audio_gate_relocked: 1,
        ios_unmute_installed: 1, ios_unmute_interrupted: 1
    };
    if (typeof window.__ae === 'function') try {
        var origAe = window.__ae;
        window.__ae = function (type, data) {
            if (!meaningless[type] && !(data && meaninglessF[data.f])) lastMeaningfulAe = Date.now();
            return origAe.apply(this, arguments);
        };
    } catch (_) {
        // __ae not writable (e.g. wrapped behind a getter-only accessor).
        // The DOM-mutation / blur / navigation signals still work; only
        // the meaningful-__ae signal is lost — an acceptable degradation.
    }

    // ── Sound: the answer this detector could not hear ───────────────────
    // A button whose whole job is a sound answers through no channel above:
    // a Web Audio source, a `new Audio()` that never enters the document (its
    // `play` event has no path to the document listener below), speech. Prod,
    // 7 days to 2026-10-01: /english-level-test «▶ sample» alone filed 2316
    // sessions as dead — its replay goes through a detached element — and the
    // «🔊» / voice / string / channel buttons of a dozen tools sat under it.
    // The first page that reads this metric (/pino/dead-clicks) opened on a
    // wall of buttons that work; that is how the real dead «● Запись» of
    // /virtual-drums stayed unread for three weeks.
    //
    // Sound counts only when THIS press can own it:
    //   · it was set off inside the handlers of this press (pointerdown or
    //     click dispatch, `ownPress` below) — a drum pad hit twice a second
    //     owns both of its sounds; OR it starts inside the press window while
    //     nothing started in the second before the press — a sequencer starts
    //     sources all the time, and a dead button pressed over it must still
    //     be reported;
    //   · a Web Audio source counts only if its context is running by the time
    //     we decide — `start()` on a suspended context is silence, which is
    //     exactly the «pressed play, heard nothing» failure to keep;
    //   · a media element only once its play() RESOLVED, unmuted;
    //   · speech only on the utterance's own `start` (no voices = silence);
    //   · service silence is not sound: __audioGate and ios-audio-unmute play
    //     silence on the first gesture of ANY page to unlock audio, and counting
    //     it would clear every dead button pressed first (`serviceSilence`).
    var QUIET_BEFORE_MS = 1000;
    var sounds = [];               // {t, ctx, own} — what started recently
    var pressAt = 0;               // the press whose event is being dispatched now
    function ownPress(at) {
        pressAt = at;
        setTimeout(function () { if (pressAt === at) pressAt = 0; }, 0);
    }
    function noteSound(ctx, own) {
        var now = Date.now();
        if (sounds.length > 64) sounds = sounds.filter(function (s) { return s.t > now - 5000; });
        // `live`: the context runs now, or starts running later (resume()
        // after start() is a common order). Taken now, not when we decide —
        // a short beep whose context is closed on `ended` did sound.
        var s = { t: now, own: own || 0, live: !ctx || ctx.state === 'running' };
        if (!s.live && ctx && ctx.addEventListener) {
            try {
                ctx.addEventListener('statechange', function on() {
                    if (ctx.state === 'running') { s.live = true; ctx.removeEventListener('statechange', on); }
                });
            } catch (_) {}
        }
        sounds.push(s);
    }
    function serviceSilence(el) {
        if (!el) return false;
        try {
            if (el.__tmbSilence) return true;               // ios-audio-unmute's element
            var g = window.__audioGate;
            return !!(g && g.isSilence && g.isSilence(el)); // __audioGate's unlock clip
        } catch (_) { return false; }
    }
    function wrapStart(proto) {
        if (!proto || !Object.prototype.hasOwnProperty.call(proto, 'start')) return;
        var orig = proto.start;
        proto.start = function () {
            var r = orig.apply(this, arguments);
            try {
                var c = this.context;
                // rendering an export is work, not something the person hears
                if (!(typeof OfflineAudioContext === 'function' && c instanceof OfflineAudioContext)) noteSound(c, pressAt);
            } catch (_) {}
            return r;
        };
    }
    try {
        // AudioBufferSourceNode declares its own start(when, offset, duration);
        // the rest inherit the base one — wrap every own definition once.
        [window.AudioScheduledSourceNode, window.AudioBufferSourceNode, window.OscillatorNode, window.ConstantSourceNode]
            .forEach(function (C) { if (C) wrapStart(C.prototype); });
    } catch (_) {}
    try {
        var origPlay = HTMLMediaElement.prototype.play;
        HTMLMediaElement.prototype.play = function () {
            var p = origPlay.apply(this, arguments), el = this, own = pressAt;
            // in the document its `play` event reaches the listener below
            if (!el.isConnected && p && typeof p.then === 'function') {
                p.then(function () { if (!el.muted && el.volume > 0 && !serviceSilence(el)) noteSound(null, own); }, function () {});
            }
            return p;
        };
    } catch (_) {}
    try {
        if (window.SpeechSynthesis && SpeechSynthesis.prototype.speak) {
            var origSpeak = SpeechSynthesis.prototype.speak;
            SpeechSynthesis.prototype.speak = function (u) {
                var own = pressAt;
                try {
                    if (u && String(u.text || '').trim() && u.volume !== 0) {
                        u.addEventListener('start', function () { noteSound(null, own); });
                    }
                } catch (_) {}
                return origSpeak.apply(this, arguments);
            };
        }
    } catch (_) {}
    /** Did a sound start that the press opened at `from` can own? */
    function soundAnswered(from) {
        var owned = false, after = false, before = false;
        for (var i = 0; i < sounds.length; i++) {
            var s = sounds[i];
            if (s.own === from) { owned = owned || s.live; continue; }
            if (s.t < from) { if (s.t >= from - QUIET_BEFORE_MS) before = true; continue; }
            if (s.live) after = true;
        }
        return owned || (after && !before);
    }

    // True for DOM nodes whose mutation is background noise, never a
    // response to a user click: ad slots, iframes and the head subtree,
    // plus non-visual script / style / link / meta elements. The ad
    // loader injects a script element into the head on every ad fail —
    // that must not be mistaken for a click effect.
    function isNoise(n) {
        if (!n || n.nodeType !== 1) return false;
        var tg = n.tagName;
        if (tg === 'SCRIPT' || tg === 'STYLE' || tg === 'LINK' || tg === 'META') return true;
        // The unlockers' silent <audio>: on an iPhone ios-audio-unmute appends
        // it to <body> on the FIRST gesture of every page. Its sound was already
        // not counted (serviceSilence); its insertion was — so the first dead
        // tap of every iPhone page read as «it did something» (measured
        // 15.10.2026 on /trim: the only DOM change in the window was this node).
        if ((tg === 'AUDIO' || tg === 'VIDEO') && serviceSilence(n)) return true;
        return !!(n.closest && n.closest('.tool-ad-slot,ins.adsbygoogle,iframe,head'));
    }

    // A `style` attribute mutation counts as a click effect ONLY when it
    // changes visibility — the `display` or `visibility` token. This catches
    // the very common "reveal a hidden panel via element.style.display='block'"
    // affordance, which the attributeFilter (class/hidden/aria) otherwise
    // misses → a false silent_click. Real-world offenders: the rating widget's
    // "Want to leave a comment?" toggle (showCommentForm sets
    // commentEl.style.display='block') and any calculator result panel shown
    // via an inline display flip. Arbitrary style churn — transform, color,
    // width/height animation, hover restyling — must NOT count, so we compare
    // only the visibility tokens, not the whole style string.
    function styleVizKey(s) {
        s = s || '';
        var d = (s.match(/display\s*:\s*([^;]+)/i) || ['', ''])[1].replace(/\s+/g, '');
        var v = (s.match(/visibility\s*:\s*([^;]+)/i) || ['', ''])[1].replace(/\s+/g, '');
        return d + '|' + v;
    }

    var CLICKABLE = 'button,a,[role=button],[onclick],.btn';
    var OBSERVE_OPTS = {
        childList: true, subtree: true, attributes: true, attributeOldValue: true,
        attributeFilter: ['class', 'hidden', 'open', 'disabled', 'value',
            'aria-expanded', 'aria-hidden', 'aria-selected', 'style',
            // A segmented control that answers by flipping aria-pressed (and
            // styles itself off it) changes nothing else — /video-bg-remover's
            // background swatches were reported dead for exactly that.
            'aria-pressed', 'aria-checked']
    };
    function keyOf(el) { return (el.id || '') + '|' + (el.className || '').toString().substring(0, 30); }

    /** Does this batch of mutations contain a real response to a click? */
    function anyEffectRecord(recs) {
        for (var i = 0; i < recs.length; i++) {
            var r = recs[i];
            if (isNoise(r.target)) continue;
            if (r.type === 'attributes') {
                // No-op re-assignment is NOT an effect. A global "close all
                // menus on any click" handler re-sets className on unrelated
                // elements on every click — the observer fires a record but the
                // value is unchanged. Without this guard the detector treats
                // every click as effective and never fires.
                if (r.oldValue === r.target.getAttribute(r.attributeName)) continue;
                // For `style`, only a visibility change (display / visibility
                // token) counts — ignore transform / colour / size churn.
                if (r.attributeName === 'style' &&
                    styleVizKey(r.oldValue) === styleVizKey(r.target.getAttribute('style'))) continue;
            } else if (r.type === 'childList') {
                var touched = [].slice.call(r.addedNodes).concat([].slice.call(r.removedNodes));
                var real = touched.some(function (x) {
                    return x.nodeType === 3 || (x.nodeType === 1 && !isNoise(x));
                });
                if (!real) continue;
            }
            return true;
        }
        return false;
    }
    function alreadySelected(el) {
        try {
            var attrs = ['aria-pressed', 'aria-selected', 'aria-checked', 'aria-current'];
            for (var i = 0; i < attrs.length; i++) {
                var v = el.getAttribute(attrs[i]);
                if (v && v !== 'false') return true;
            }
            var c = typeof el.className === 'string' ? el.className : '';
            return /(^|\s)(active|on|is-active|selected|checked|current)(\s|$)/.test(c);
        } catch (_) { return false; }
    }
    function clickableFor(e) {
        var el = e.target && e.target.closest && e.target.closest(CLICKABLE);
        if (!el) return null;
        if (el.disabled || el.getAttribute('aria-disabled') === 'true') return null;
        return el;
    }

    /* ⚠️⚠️ The window has to open when the press STARTS, not when it ends.
       A whole family of tools answers `pointerdown` — drum pads, piano keys,
       canvas games — and the answer is a flash that is over in ~90 ms. Watch
       from the `click` event instead and you are looking at an empty stage:
       hold the button a moment longer than the flash and a working instrument
       is filed as a dead button. Measured 2026-09-18 on /virtual-drums with a
       control that flashes for 90 ms on pointerdown: held 20 ms and 80 ms it
       passes, held 200 ms and 600 ms it is reported dead, every time. The tool
       carried 378 sessions of exactly that, and every one of those sessions had
       played a drum. So the watch opens at pointerdown and the click adopts it. */
    var pendingWatch = null;
    function startWatch(key) {
        var w = { key: key, at: Date.now(), effect: false, mo: null };
        try {
            w.mo = new MutationObserver(function (recs) { if (anyEffectRecord(recs)) w.effect = true; });
            w.mo.observe(document.documentElement, OBSERVE_OPTS);
        } catch (_) {}
        return w;
    }
    function stopWatch(w) { try { if (w && w.mo) w.mo.disconnect(); } catch (_) {} }

    document.addEventListener('pointerdown', function (e) {
        var el = clickableFor(e);
        if (!el) return;
        var k = keyOf(el);
        if (seen[k]) return;
        if (pendingWatch) stopWatch(pendingWatch);
        pendingWatch = startWatch(k);
        ownPress(pendingWatch.at);    // a pad that sounds on pointerdown owns that sound
        // a press that never becomes a click (drag, scroll, long-press menu)
        // must not leave an observer running for the rest of the page's life
        setTimeout(function () {
            if (pendingWatch && pendingWatch.at <= Date.now() - 2400) { stopWatch(pendingWatch); pendingWatch = null; }
        }, 2500);
    }, true);

    /* ⚠️⚠️ A press on a DISABLED button is the deadest press there is — and
       until 2026-11-04 this judge never saw one. `clickableFor` drops disabled
       targets, and it could not have judged them anyway: the browser never
       delivers `click` to a disabled control (measured 04.11: Chromium mouse
       and touch, Firefox and WebKit mouse — `pointerdown`/`pointerup` reach the
       document with the button as the target, `click` never does). So the
       telemetry had no row for the commonest dead end on the site: a button
       waiting for a condition the page never names. Bug report 31.10.2026 on
       /virtual-orchestra («why doesn't "Assemble" turn on?», one part loaded,
       nine minutes on the page): the reason lived in the click handler, i.e.
       nowhere, and the only text near the button was the limit counter. Of
       2849 rage_click rows in 20 days, 22 carried dis=1 — all of them Leaflet
       links with aria-disabled; not one native disabled button.
       Judged like any press — the same 2 s effect window opened at
       pointerdown — so a page that ANSWERS the press (names the missing
       condition, as the orchestra now does) is not reported; one that stays
       mute is reported as det:3 with dis:1. Only native `disabled`:
       aria-disabled controls receive clicks and stay with the path below
       (and are, by convention, limits like a zoom at its maximum). */
    var deadPress = null;
    document.addEventListener('pointerdown', function (e) {
        var el = e.target && e.target.closest && e.target.closest(CLICKABLE);
        if (!el || el.disabled !== true) return;
        var k = 'dis:' + keyOf(el);
        if (seen[k]) return;
        if (deadPress) stopWatch(deadPress.w);
        deadPress = { el: el, k: k, w: startWatch(k) };
    }, true);
    document.addEventListener('pointercancel', function () {
        if (deadPress) { stopWatch(deadPress.w); deadPress = null; }
    }, true);
    document.addEventListener('pointerup', function (e) {
        var d = deadPress;
        deadPress = null;
        if (!d) return;
        // a press that slid off the button (or turned into a scroll) is no press on it
        var el = e.target && e.target.closest && e.target.closest(CLICKABLE);
        if (el !== d.el) { stopWatch(d.w); return; }
        setTimeout(function () {
            stopWatch(d.w);
            if (d.w.effect || seen[d.k]) return;
            if (soundAnswered(d.w.at)) return;
            if (lastMeaningfulAe > d.w.at) return;
            seen[d.k] = 1;
            try { document.dispatchEvent(new CustomEvent('tmb:silent-click', { detail: { el: d.el, sel: 0, dis: 1 } })); } catch (_) {}
            if (!report) return;
            try {
                var clsRaw = d.el.className;
                window.__ae('feature_use', {
                    slug: slug,
                    f: 'silent_click',
                    det: 3,
                    dis: 1,
                    target_id: d.el.id || '',
                    target_aria: (d.el.getAttribute('aria-label') || d.el.getAttribute('title') || '').substring(0, 40),
                    target_cls: (typeof clsRaw === 'string' ? clsRaw : (clsRaw && clsRaw.baseVal) || '').substring(0, 40),
                    target_txt: (d.el.textContent || '').substring(0, 30).trim(),
                    tag: (d.el.tagName || '').toLowerCase(),
                    sel: 0,
                    time_on_page_s: Math.round(performance.now() / 1000),
                    _b: br, mob: mob
                });
            } catch (_) {}
        }, 2000);
    }, true);

    document.addEventListener('click', function (e) {
        var el = clickableFor(e);
        if (!el) return;
        var id = el.id || '';
        var key = keyOf(el);
        if (seen[key]) { if (pendingWatch && pendingWatch.key === key) { stopWatch(pendingWatch); pendingWatch = null; } return; }

        var clickAt = Date.now();
        // Start of THIS click's propagation, taken in the capture phase above —
        // i.e. before the uploader's element-level handler ran input.click().
        // Comparing against clickAt would be wrong: by the time this bubble
        // handler runs, the picker click already happened a hair earlier.
        var dispatchAt = lastDispatchAt;
        var startHref = location.href;
        // adopt the watch opened at pointerdown; a keyboard-activated click
        // (Enter/Space, detail === 0) never had one, so open it now
        var watch = (pendingWatch && pendingWatch.key === key) ? pendingWatch : startWatch(key);
        pendingWatch = null;
        ownPress(watch.at);
        function mark() { watch.effect = true; }

        // 1. Real DOM mutation — modal/toast/dropdown insert, banner
        //    removal, class/aria/hidden flip (play button to playing,
        //    tab switch, accordion). attributeFilter keeps it to genuine
        //    UI-state attrs; isNoise() drops ad/head/script churn.
        //    The observer itself belongs to `watch` and was opened at
        //    pointerdown — see startWatch()/anyEffectRecord().

        // 1b. input/change event on any <input>/<textarea>/<select> after
        //     the click counts as effect — covers number-pad / digit-key /
        //     keypad UIs where the response is `targetInput.value = ...`
        //     (an HTMLInputElement.value DOM-property write, NOT the value
        //     ATTRIBUTE — MutationObserver only sees the attribute). Top
        //     false-positive source on /iq-test was 188 silent_click events
        //     on the Clear button + 600+ on digit keys, all hitting a real
        //     <input>.value update that the attribute observer never sees.
        function onInputEvent() { mark(); }
        document.addEventListener('input', onInputEvent, true);
        document.addEventListener('change', onInputEvent, true);

        // 1b-bis. …and the OTHER half of the same family: a page that writes
        //     `el.value = x` itself fires NO event and changes NO attribute, so
        //     neither the observer above nor the listeners just installed see
        //     anything. A stepper is the plain case — /virtual-drums' ± BPM
        //     buttons move the tempo on every click and were reported dead for
        //     it. Snapshot what the fields hold at click time and compare when
        //     we decide; a page has a handful of them, and we only get here
        //     once per button (seen[key] above).
        var fields = [], before = [];
        try {
            fields = [].slice.call(document.querySelectorAll('input,select,textarea'), 0, 400);
            before = fields.map(function (f) { try { return f.type === 'checkbox' || f.type === 'radio' ? (f.checked ? '1' : '0') : f.value; } catch (_) { return null; } });
        } catch (_) {}
        function fieldChanged() {
            for (var i = 0; i < fields.length; i++) {
                var now; try { now = fields[i].type === 'checkbox' || fields[i].type === 'radio' ? (fields[i].checked ? '1' : '0') : fields[i].value; } catch (_) { continue; }
                if (now !== before[i]) return true;
            }
            return false;
        }

        // 1c. Two whole families of working affordance leave NO trace the
        //     observers above can see, and they were the top of the
        //     leaderboard — the exact disease the v2 rewrite existed to cure,
        //     recurring with a different cause.
        //
        //     · FULLSCREEN. `requestFullscreen()` changes no attribute, adds no
        //       node, steals no focus and emits no telemetry; only the
        //       `:fullscreen` pseudo-class flips, which a MutationObserver
        //       cannot see. Every successful fullscreen click on the site was
        //       counted as a dead button.
        //     · MEDIA STATE. Play / pause / mute / volume are property writes.
        //       /screen-share's mute button, for instance, only sets
        //       `style.opacity`, and styleVizKey() deliberately ignores
        //       non-visibility style churn.
        //
        //     Measured on prod, 30 days: 5573 silent_click events in total, of
        //     which scs-fs-btn alone was 670 (12% of the whole metric) and
        //     scs-mute-btn 117; fullscreen/mute/volume-shaped ids 820 (14.7%),
        //     and the play buttons below them (play-btn, atnPlayBtn, bgmPreview,
        //     smmPlayBtn, play-after) add ~245 more. A metric whose loudest
        //     entry is a working button hides the dead ones underneath it, and
        //     it rides into every feedback report as `last_action`.
        //
        //     ⚠️ `timeupdate` and `seeking` are deliberately NOT here: they fire
        //     on their own during playback and on every loop restart, which
        //     would make ANY click look effective while media is playing — a
        //     detector that never fires is worse than one that fires wrongly.
        //     The four kept events are all user-driven state changes.
        function onFsChange() { if (document.fullscreenElement || document.webkitFullscreenElement) mark(); }
        document.addEventListener('fullscreenchange', onFsChange);
        document.addEventListener('webkitfullscreenchange', onFsChange);
        // iOS video-only fullscreen never fires fullscreenchange.
        document.addEventListener('webkitbeginfullscreen', mark, true);
        // ⚠️ not the unlockers' silence (see serviceSilence above): on iOS the
        // first tap of every page plays a silent <audio> that lives in the DOM.
        function onMediaState(e) { if (!serviceSilence(e && e.target)) mark(); }
        ['play', 'pause', 'volumechange', 'ratechange'].forEach(function (ev) {
            document.addEventListener(ev, onMediaState, true);
        });
        //     · POPOVER. `popovertarget` opens and closes a native popover by
        //       flipping `:popover-open` — no attribute, no node — so the
        //       observer sees nothing, the same blind spot as fullscreen. The
        //       element answers with a `toggle` event (it does not bubble, hence
        //       the capture listener). Measured: /video-bg-remover's «Параметры
        //       вывода» and the popover's own «Готово» — ~360 sessions over three
        //       weeks reported dead, every click having opened or closed it.
        document.addEventListener('toggle', mark, true);

        // 2. Native dialog (file picker / print / auth) steals focus.
        window.addEventListener('blur', mark, { once: true });
        // 3. On-page (SPA-style) navigation.
        window.addEventListener('hashchange', mark, { once: true });
        window.addEventListener('popstate', mark, { once: true });

        setTimeout(function () {
            stopWatch(watch);
            window.removeEventListener('blur', mark);
            window.removeEventListener('hashchange', mark);
            window.removeEventListener('popstate', mark);
            document.removeEventListener('fullscreenchange', onFsChange);
            document.removeEventListener('webkitfullscreenchange', onFsChange);
            document.removeEventListener('webkitbeginfullscreen', mark, true);
            ['play', 'pause', 'volumechange', 'ratechange'].forEach(function (ev) {
                document.removeEventListener(ev, onMediaState, true);
            });
            document.removeEventListener('toggle', mark, true);
            document.removeEventListener('input', onInputEvent, true);
            document.removeEventListener('change', onInputEvent, true);
            if (watch.effect) return;              // observed a real effect
            if (soundAnswered(watch.at)) return;   // it answered with sound
            if (fieldChanged()) return;            // the page wrote a field value itself
            if (lastPickerOpenAt >= dispatchAt) return; // opened the file picker
            if (lastMeaningfulAe > clickAt) return; // meaningful telemetry fired
            if (location.href !== startHref) return; // navigated
            seen[key] = 1;
            var wasSelected = alreadySelected(el) ? 1 : 0;
            // The page's own reader (the intl showcase answers a dead press
            // before a file). Same verdict as the report — one judge, two readers.
            try { document.dispatchEvent(new CustomEvent('tmb:silent-click', { detail: { el: el, sel: wasSelected } })); } catch (_) {}
            if (!report) return;
            try {
                // SVGAnimatedString.toString() returns "[object
                // SVGAnimatedString]" — useless. Use .baseVal for SVG
                // elements (production session 87730c27 logged that
                // gibberish from a sticky-ad close click).
                var clsRaw = el.className;
                var cls = (typeof clsRaw === 'string' ? clsRaw : (clsRaw && clsRaw.baseVal) || '').substring(0, 40);
                window.__ae('feature_use', {
                    slug: slug,
                    f: 'silent_click',
                    det: 2,
                    target_id: id,
                    target_aria: (el.getAttribute('aria-label') || el.getAttribute('title') || '').substring(0, 40),
                    target_cls: cls,
                    target_txt: (el.textContent || '').substring(0, 30).trim(),
                    // What the reader of /pino/dead-clicks needs to judge a row:
                    // a link (a page that takes >2 s to load looks dead here),
                    // and a target that was ALREADY selected — a second click
                    // on the current tab changes nothing, and that is no defect.
                    tag: (el.tagName || '').toLowerCase(),
                    sel: wasSelected,
                    time_on_page_s: Math.round(performance.now() / 1000),
                    _b: br, mob: mob
                });
            } catch (_) {}
        }, 2000);
    }, true);

    // 'armed' — judging and reporting; 'answers' — judging for the showcase
    // only, nothing is reported (the consent gate or __ae said no).
    window.__scdStatus = report ? 'armed' : 'answers';
})();
